import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Col, Row, Spin, Table, Typography, VertContainer, VertIcon, VertSteps,
} from '@vertem-core/vertice-ds';
import { GetRedemptionInfo } from 'redux/Reducers/ReportRedemption/ReportRedemptionSlice';
import { LuClock10, LuShoppingCart } from 'react-icons/lu';
import dayjs from 'dayjs';
import { OPTIONS_ORDERS_STATUS } from '../Constants';
import useScreenSize from 'Hooks/useScreenSize';
import { handleAddressNumberOutput } from 'Utils/commons/address';

const OrderDetailsModal = ({ redemptionId }) => {
  const dispatch = useDispatch();
  const { screenWidth } = useScreenSize()
  const {
    allParticipantsLoginsOfWallet,
    isLoadingOrderDetails,
    orderDetailsInfo,
  } = useSelector((state) => state.ReportRedemption);

  useEffect(() => {
    dispatch(GetRedemptionInfo({ redemptionId, walletsLogin: allParticipantsLoginsOfWallet }));
  }, [redemptionId, allParticipantsLoginsOfWallet]);

  const returnValueInPoints = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value).replace('R$', '');
  };

  const firstRow = [
    { label: 'Data do Pedido', value: dayjs(orderDetailsInfo.orderDate).format('DD/MM/YYYY') },
    { label: 'ID Pedido Pai', value: orderDetailsInfo.parentOrderId },
    { label: 'Valor total', value: returnValueInPoints(orderDetailsInfo.totalValue) },
    { label: 'Valor do Frete', value: returnValueInPoints(orderDetailsInfo.shippingValue) },
    {
      label: 'Status',
      value: Object.values(OPTIONS_ORDERS_STATUS).find(
        (status) => status.value === orderDetailsInfo?.statusId,
      )?.label,

      fontColor: Object.values(OPTIONS_ORDERS_STATUS).find(
        (status) => status.value === orderDetailsInfo.statusId,
      )?.color,
    },
  ];

  const secondRow = [
    { label: 'Nome do Participante', value: orderDetailsInfo.participantName },
    { label: 'Fornecedor', value: orderDetailsInfo.supplierName },
  ];

  const columns = [
    {
      title: 'Produto',
      dataIndex: 'redemption-product',
      key: 'redemption-product',
      render: (_, { productName }) => (
        <Typography.Text>{productName}</Typography.Text>
      ),
      width: '35%',
    },
    {
      title: 'Valor Unitário',
      dataIndex: 'redemption-value',
      key: 'redemption-value',
      render: (_, { redemptionValue }) => (
        <Typography.Text>
          {`${returnValueInPoints(redemptionValue)} pontos`}
        </Typography.Text>
      ),
      width: '30%',
    },
    {
      title: 'Quantidade',
      dataIndex: 'redemption-product-quantity',
      key: 'redemption-product-quantity',
      render: (_, { redemptionProductQuantity }) => (
        <Typography.Text>{redemptionProductQuantity}</Typography.Text>
      ),
      width: '12%',
    },
    {
      title: 'Valor total',
      dataIndex: 'total-value',
      key: 'total-value',
      render: (_, { totalValue }) => (
        <Typography.Text>
          {`${returnValueInPoints(totalValue)} pontos`}
        </Typography.Text>
      ),
      width: '23%',
    },
  ];

  const tableOfProducts = () => {
    const products = orderDetailsInfo?.products?.map((product) => ({
      key: product.name.replace(/\s/g, ''),
      productName: product.name,
      redemptionValue: product.unitPrice,
      redemptionProductQuantity: product.quantity,
      totalValue: product.unitPrice * product.quantity,
    }));
    return products;
  };

  const orderHistorySteps = () => {
    if (
      !orderDetailsInfo
      || !orderDetailsInfo?.statusHistories
      || !orderDetailsInfo?.statusHistories?.length
    ) return [];
    const orderHistory = orderDetailsInfo?.statusHistories;
    const reversedOrderHistory = [];
    // eslint-disable-next-line no-nested-ternary

    for (let i = orderHistory.length - 1; i >= 0; i--) {
      reversedOrderHistory.push(orderHistory[i]);
    }
    return reversedOrderHistory?.map((item) => ({
      title: item.status,
      description: dayjs(item.statusDate).format('DD/MM/YYYY'),
      id: item.id,
    }));
  };

  return (
    <VertContainer className="padding-x-regular">
      <Typography.Title type="highlight" className="color-highlight">{`Pedido #${redemptionId}`}</Typography.Title>
      <Row gutter={[16, 16]}>
        {firstRow.map((item) => (
          <Col lg={4} md={6} sm={8} xs={12}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography.Text className="color-highlight" strong>{item.label}</Typography.Text>
              {isLoadingOrderDetails ? (
                <Typography.Text>Carregando...</Typography.Text>
              ) : (
                <Typography.Text
                  strong
                  style={{ color: `${item.fontColor ?? 'unset'}` }}
                >
                  {item.value}
                </Typography.Text>
              )}
            </div>
          </Col>
        ))}
      </Row>

      <Row className="margin-top-small" gutter={[16, 16]}>
        {secondRow.map((item) => (
          <Col lg={4} md={6} sm={8} xs={12}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Typography.Text
                className="color-highlight"
                strong
              >
                {item.label}
              </Typography.Text>
              {isLoadingOrderDetails ? (
                <Typography.Text>Carregando...</Typography.Text>
              ) : (
                <Typography.Text>{item.value}</Typography.Text>
              )}
            </div>
          </Col>
        ))}
        <Col lg={16} md={12} sm={8} xs={24}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography.Text className="color-highlight" strong>End. de Entrega</Typography.Text>
            {isLoadingOrderDetails ? (
              <Typography.Text>Carregando...</Typography.Text>
            ) : (
              <Typography.Text>
                {`${orderDetailsInfo?.deliveryAddress?.address}, ${handleAddressNumberOutput(orderDetailsInfo?.deliveryAddress?.number)}, ${orderDetailsInfo?.deliveryAddress?.complement} - ${orderDetailsInfo?.deliveryAddress?.city}/${orderDetailsInfo?.deliveryAddress?.state} - ${orderDetailsInfo?.deliveryAddress?.zipCode}`}
              </Typography.Text>
            )}
          </div>
        </Col>
      </Row>

      <VertContainer className="margin-top-small">
        <VertIcon component={LuClock10} size="large" className="margin-top-small" />
        <Typography.Text className="txt-big color-highlight margin-left-extra-small">
          Histórico
        </Typography.Text>
      </VertContainer>
      <div
        className="padding-y-small padding-x-small margin-top-small"
        style={{
          boxShadow: '0px 2px 8px 0px rgba(0, 0, 20, 0.12)',
          borderRadius: 'var(--border-radius-secondary)',
          backgroundColor: '#EDEDED',
        }}
      >
        {isLoadingOrderDetails ? (
          <Typography.Text>
            Carregando o histórico...
          </Typography.Text>
        ) : (
          <VertSteps
            items={orderHistorySteps()}
            progressDot
            current={orderHistorySteps().findIndex(
              (item) => item.title === orderDetailsInfo?.statusHistories[0]?.status,
            )}
            status={[
              OPTIONS_ORDERS_STATUS.REFUNDED.value,
              OPTIONS_ORDERS_STATUS.AUTHENTICATION_DENIED.value,
              OPTIONS_ORDERS_STATUS.DENIED_BY_SUPPLIER.value,
              OPTIONS_ORDERS_STATUS.NOT_COMPLETED.value,
              OPTIONS_ORDERS_STATUS.PAYMENT_DENIED.value,
              OPTIONS_ORDERS_STATUS.PAYMENT_REFUNDED.value,
              OPTIONS_ORDERS_STATUS.REFUNDED.value,
              OPTIONS_ORDERS_STATUS.RISK_ANALYSIS_DENIED.value,
            ].includes(orderHistorySteps()[orderHistorySteps().length - 1]?.id) ? 'error' : 'success'}
          />
        )}
      </div>
      <VertContainer className="margin-top-small">
        <VertIcon component={LuShoppingCart} size="large" className="margin-top-small" />
        <Typography.Text className="txt-big color-highlight margin-left-extra-small">
          Produtos
        </Typography.Text>
      </VertContainer>

      <VertContainer className="margin-top-extra-small">
        <Table
          locale={{
            emptyText: 'Nenhum resultado encontrado',
          }}
          loading={isLoadingOrderDetails}
          columns={columns}
          dataSource={tableOfProducts()}
          pagination={false}
          bordered
          tableLayout="auto"
          sticky
          size="small"
          scroll={{ x: 1000 }}
        />
      </VertContainer>
      <VertContainer className="margin-top-small">
        <Row gutter={[16, 16]}>
          <Col lg={12} md={12} sm={12} xs={24}>
            <Typography.Title>VALOR TOTAL</Typography.Title>
          </Col>
          <Col style={{ display: 'flex', justifyContent: screenWidth > 600 && 'flex-end' }} lg={12} md={12} sm={12} xs={24}>
            {isLoadingOrderDetails ? (
              <Spin />
            ) : (
              <Typography.Title>
                {`${returnValueInPoints(orderDetailsInfo?.totalValue)} pontos`}
              </Typography.Title>
            )}
          </Col>
        </Row>
      </VertContainer>
    </VertContainer>
  );
};

export default OrderDetailsModal;
